// src/utils/stateHelpers.js
// Single canonical source for all state derivation and display constants.
// Import from here — never define STATE_COLORS/STATE_BG/STATE_LABELS locally.

// ─── Story status helpers ─────────────────────────────────────────────────────
// Per ADR-026: stories.status is a three-state enum: 'unread' | 'read' | 'reviewed'
// No more derived display state — use story.status directly.

export function canReview(story) {
  return story?.status === 'read';
}

// ─── Character learning state constants ──────────────────────────────────────
// Per ADR-023: three states — to_learn, practicing, known
// Colours confirmed from Figma (PRODUCTION_DESIGN.md Section 1.1).
// In Figma, 'to_learn' is labelled "New Today".

export const CHARACTER_STATE_COLORS = {
  to_learn: '#e8634f',   // celebration-orange (Figma: New Today)
  practicing: '#2ab8a8', // teal
  known: '#766855',      // dark-cream
};

export const CHARACTER_STATE_BG = {
  to_learn: 'rgba(255, 104, 33, 0.2)',   // #ff6821 wash (Figma: New Today bg)
  practicing: 'rgba(42, 184, 168, 0.2)', // teal wash
  known: 'transparent',                  // no background for Known
};

export const CHARACTER_STATE_LABELS = {
  to_learn: 'New',
  practicing: 'Practicing',
  known: 'Known',
};

export function getCharacterColor(state) {
  return CHARACTER_STATE_COLORS[state] ?? CHARACTER_STATE_COLORS.to_learn;
}

export function getCharacterBg(state) {
  return CHARACTER_STATE_BG[state] ?? CHARACTER_STATE_BG.to_learn;
}

export function getCharacterLabel(state) {
  return CHARACTER_STATE_LABELS[state] ?? 'New';
}

// ─── display_state constants (story context — ADR-043) ────────────────────────
// Used wherever backend returns display_state (currently: GET /stories/:id).
// Four values: known | practicing | new_introduction | reinforcement.
// Non-story surfaces continue to use the CHARACTER_STATE_* exports above.

export const DISPLAY_STATE_COLORS = {
  known: '#766855',             // dark-cream — matches CHARACTER_STATE_COLORS.known
  practicing: '#2ab8a8',        // teal — matches CHARACTER_STATE_COLORS.practicing
  new_introduction: '#e8634f',  // celebration-orange — matches CHARACTER_STATE_COLORS.to_learn
  reinforcement: '#e8634f',     // same orange: visual treatment for "actively teaching"
};

export const DISPLAY_STATE_BG = {
  known: 'transparent',
  practicing: 'rgba(42, 184, 168, 0.2)',
  new_introduction: 'rgba(255, 104, 33, 0.2)',
  reinforcement: 'rgba(255, 104, 33, 0.2)',
};

export const DISPLAY_STATE_LABELS = {
  known: 'Known',
  practicing: 'Practicing',
  new_introduction: 'New',
  reinforcement: 'New',
};

export function getDisplayStateColor(displayState) {
  return DISPLAY_STATE_COLORS[displayState] ?? DISPLAY_STATE_COLORS.new_introduction;
}

export function getDisplayStateBg(displayState) {
  return DISPLAY_STATE_BG[displayState] ?? DISPLAY_STATE_BG.new_introduction;
}

export function getDisplayStateLabel(displayState) {
  return DISPLAY_STATE_LABELS[displayState] ?? 'New';
}
